import React from "react";
import { ChevronDown, LayoutDashboard, LogOut, User } from "lucide-react";
import { Button, ButtonLink } from "../ui/button";
import { AppLink } from "../ui/link";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { HeaderSignOutButton } from "./HeaderSignOutButton";
import type { RouteMap, UserDisplay } from "../../pages/MainAppPage/types";
import { mobileScrollLinkClass, scrollLinkClass } from "./navLinkStyles";
import { useCurrentPath } from "../../hooks";

export type NavLayoutVariant = "desktop" | "mobile";

export type HeaderAuthLinksProps = {
  layout: NavLayoutVariant;
  loggedIn: boolean;
  hasBusiness: boolean;
  hasOrganization?: boolean;
  canAccessBackend?: boolean;
  routes: RouteMap;
  userDisplay?: UserDisplay | null;
  onSignOut?: () => void;
};

function isAppShellPath(currentPath: string, routes: RouteMap): boolean {
  const dashboard = routes.dashboard ?? "/dashboard";
  const organization = routes.organization ?? "/organization";
  const onboarding = routes.onboarding ?? "/onboarding";
  const teammate = routes.teammateHome ?? "/teammate";

  return (
    currentPath === dashboard ||
    currentPath.startsWith(`${dashboard}/`) ||
    currentPath === organization ||
    currentPath.startsWith(`${organization}/`) ||
    currentPath === onboarding ||
    currentPath === teammate
  );
}

export const HeaderAuthLinks: React.FC<HeaderAuthLinksProps> = ({
  layout,
  loggedIn,
  hasBusiness,
  hasOrganization,
  canAccessBackend,
  routes,
  userDisplay,
  onSignOut,
}) => {
  const currentPath = useCurrentPath();

  const isMobile = layout === "mobile";
  const authLinkClass = isMobile
    ? `${mobileScrollLinkClass} flex items-center`
    : "flex items-center font-medium text-muted-foreground hover:text-foreground transition-colors no-underline hover:no-underline";
  const loginLinkClass = isMobile
    ? `${mobileScrollLinkClass} flex items-center`
    : `${scrollLinkClass} flex items-center`;
  const profileClass = isMobile
    ? `${mobileScrollLinkClass} flex items-center gap-3`
    : "flex items-center gap-3 font-medium text-muted-foreground hover:text-foreground transition-colors no-underline hover:no-underline";

  if (loggedIn) {
    const backend = Boolean(canAccessBackend);
    const showProfile =
      Boolean(userDisplay) &&
      Boolean(hasBusiness || hasOrganization) &&
      isAppShellPath(currentPath, routes);
    const orgHomePath = routes.organization ?? "/organization";
    const orgSetupPath = routes.organizationSetup ?? "/organization/setup";
    const teammatePath = routes.teammateHome ?? "/teammate";
    const setupPaths = [orgSetupPath, orgHomePath, routes.onboarding];
    const onSetupPage = setupPaths.includes(currentPath);

    let headerAuthLink = null;
    if (showProfile && userDisplay && !isMobile) {
      headerAuthLink = (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className={`${profileClass} cursor-pointer rounded-lg px-2 py-1 hover:bg-muted/50 focus:outline-none`}
              aria-label="Open profile menu"
            >
              <div className="max-sm:hidden sm:flex flex-col items-end">
                <span className="text-sm font-semibold text-foreground">
                  {userDisplay.userName}
                </span>
                <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                  {userDisplay.businessName}
                </span>
              </div>
              {userDisplay.avatarUrl ? (
                <img
                  src={userDisplay.avatarUrl}
                  alt=""
                  className="w-9 h-9 rounded-full border border-border object-cover shrink-0"
                />
              ) : (
                <div className="w-9 h-9 rounded-full bg-muted border border-border flex items-center justify-center text-muted-foreground shrink-0">
                  <User className="w-4 h-4" />
                </div>
              )}
              <ChevronDown className="h-4 w-4 shrink-0" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-56">
            <p
              className="truncate px-2.5 pt-2 pb-1 text-xs text-muted-foreground"
              title={userDisplay.email}
            >
              {userDisplay.email}
            </p>
            <DropdownMenuItem
              className="gap-2 text-destructive focus:text-destructive"
              onSelect={() => onSignOut?.()}
            >
              <LogOut className="h-4 w-4 shrink-0" />
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      );
    } else if (showProfile && userDisplay) {
      headerAuthLink = (
        <AppLink to={routes.dashboard} className={profileClass}>
          <div className="max-sm:hidden sm:flex flex-col items-end">
            <span className="text-sm font-semibold text-foreground">
              {userDisplay.userName}
            </span>
            <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
              {userDisplay.businessName}
            </span>
          </div>
          {userDisplay.avatarUrl ? (
            <img
              src={userDisplay.avatarUrl}
              alt=""
              className="w-9 h-9 rounded-full border border-border object-cover shrink-0"
            />
          ) : (
            <div className="w-9 h-9 rounded-full bg-muted border border-border flex items-center justify-center text-muted-foreground shrink-0">
              <User className="w-4 h-4" />
            </div>
          )}
        </AppLink>
      );
    } else if (hasBusiness && backend) {
      headerAuthLink = (
        <AppLink to={routes.dashboard} className={authLinkClass}>
          <LayoutDashboard
            className={
              isMobile ? "w-5 h-5 mr-3 text-muted-foreground" : "w-4 h-4 mr-2"
            }
          />
          Dashboard
        </AppLink>
      );
    } else if (hasOrganization && backend) {
      headerAuthLink = (
        <AppLink to={routes.onboarding} className={authLinkClass}>
          <LayoutDashboard
            className={
              isMobile ? "w-5 h-5 mr-3 text-muted-foreground" : "w-4 h-4 mr-2"
            }
          />
          Home
        </AppLink>
      );
    } else if (hasOrganization && !backend) {
      headerAuthLink = (
        <>
          <AppLink to={teammatePath} className={authLinkClass}>
            <LayoutDashboard
              className={
                isMobile ? "w-5 h-5 mr-3 text-muted-foreground" : "w-4 h-4 mr-2"
              }
            />
            Home
          </AppLink>
          <HeaderSignOutButton
            className={authLinkClass}
            isMobile={isMobile}
            onSignOut={onSignOut}
          />
        </>
      );
    } else if (!hasBusiness && !onSetupPage) {
      headerAuthLink = (
        <AppLink to={routes.onboarding} className={authLinkClass}>
          <LayoutDashboard
            className={
              isMobile ? "w-5 h-5 mr-3 text-muted-foreground" : "w-4 h-4 mr-2"
            }
          />
          Continue setup
        </AppLink>
      );
    } else if (!hasBusiness && onSetupPage && onSignOut) {
      headerAuthLink = (
        <Button variant="ghost" onClick={onSignOut} className={authLinkClass}>
          <LogOut className="w-4 h-4 shrink-0" />
          Sign out
        </Button>
      );
    }

    return headerAuthLink;
  }

  return (
    <>
      <AppLink to={routes.login} className={loginLinkClass}>
        Log in
      </AppLink>
      <ButtonLink
        to={routes.signup}
        variant="premium"
        className={isMobile ? "w-full justify-center mt-2" : undefined}
        size={isMobile ? undefined : "default"}
      >
        Start 30-day trial
      </ButtonLink>
    </>
  );
};
